import { pipe, uniqueBy, isNullish } from 'remeda'
import type { Dimension, Dimensions, Encoding, VChartSpecPipe, Tooltip } from 'src/types'
import type { Datum, ISpec, ITooltipLinePattern } from '@visactor/vchart'
import { BinEndMeasureId, BinStartMeasureId, ColorEncoding, FoldMeasureValue, XEncoding } from 'src/dataReshape'
import { getTooltipStyle } from './tooltipStyle'
import { getDefaultValueFormatterOfHistogram, getDefaultXFormatterOfHistogram } from '../../utils/histogram'
import { updateMarkTooltipElement } from './tooltipElement'

const VCHART_OUTLIER_KEY = '__VCHART_BOX_PLOT_OUTLIER_VALUE'

export const tooltipHistogram: VChartSpecPipe = (spec, context) => {
  const result = { ...spec }
  const { advancedVSeed } = context
  const { chartType, dimensions = [], encoding } = advancedVSeed
  const baseConfig = advancedVSeed.config[chartType] as { tooltip: Tooltip; binValueType: 'count' | 'percentage' }
  const { binValueType, tooltip = { enable: true } } = baseConfig
  const { enable } = tooltip
  const defaultXFormatter = getDefaultXFormatterOfHistogram(advancedVSeed)
  const defaultValueFormatter = getDefaultValueFormatterOfHistogram(binValueType)

  result.tooltip = {
    style: getTooltipStyle(tooltip),
    visible: enable,
    mark: {
      title: {
        visible: false,
      },
      content: createMarkContent(
        encoding.tooltip || [],
        dimensions,
        encoding as Encoding,
        defaultXFormatter,
        defaultValueFormatter,
      ),
    },
    dimension: {
      title: {
        visible: false,
      },
      content: createMarkContent(
        encoding.tooltip || [],
        dimensions,
        encoding as Encoding,
        defaultXFormatter,
        defaultValueFormatter,
      ),
    },

    updateElement: updateMarkTooltipElement,
  }
  return result as unknown as ISpec
}

const createMarkContent = (
  tooltip: string[],
  dimensions: Dimensions,
  encoding: Encoding,
  dimFormatter: (value: number) => string,
  defaultValueFormatter: (value: number | string) => string,
) => {
  const dims = pipe(
    dimensions.filter((item) => tooltip.includes(item.id)),
    uniqueBy((item: Dimension) => item.id),
    uniqueBy((item: Dimension) => item.alias),
  )

  const dimContent = dims.map((item: Dimension) => ({
    visible: true,
    hasShape: true,
    shapeType: 'rectRound',
    key: item.alias ?? item.id,
    value: (datum: Datum | undefined) => {
      if (!isNullish(datum?.[VCHART_OUTLIER_KEY])) {
        if (encoding.color?.includes(item.id)) {
          return datum?.[ColorEncoding] as string
        }
        if (encoding.x?.includes(item.id)) {
          return datum?.[XEncoding] as string
        }
      }

      return defaultValueFormatter(datum?.[item.id] as string)
    },
  }))

  const defaultContent = [
    {
      visible: true,
      hasShape: true,
      shapeType: 'rectRound',
      key: (datum: Datum | undefined) => {
        if (!datum) {
          return ''
        }
        return `[${dimFormatter(+datum[BinStartMeasureId])}, ${dimFormatter(+datum[BinEndMeasureId])})`
      },
      value: (datum: Datum | undefined) => {
        if (!datum) {
          return ''
        }
        return defaultValueFormatter(datum[FoldMeasureValue] as string | number)
      },
    },
  ]

  return [...dimContent, defaultContent] as ITooltipLinePattern[]
}
